import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/auth-context";
import { FileText, DollarSign, ArrowUpRight, ShoppingCart, Wallet } from "lucide-react";
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import { fetchWithAuth } from "@/lib/auth-client";
import { formatAmount, formatCompactCurrency, formatDate } from "@/lib/format";
import { TransactionStatusBadge } from "@/components/transaction-status-badge";
import { PR_STATUS_CONFIG } from "@/lib/status-config";
import type {
  DashboardKPIsDto,
  SpendBreakdownDto,
  StatusSummaryDto,
  PurchaseRequestListItemDto,
  NotificationDto,
} from "@procureflow/shared-types";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Dashboard — ProcureFlow" },
      {
        name: "description",
        content:
          "Procurement dashboard with KPIs, spend analytics, approvals and vendor performance.",
      },
    ],
  }),
  component: Dashboard,
});

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function periodStartToSinceLabel(periodStart: string): string {
  const [_year, month, day] = periodStart.split("-").map(Number);
  return `Since ${MONTH_NAMES[(month ?? 1) - 1]} ${day}`;
}

// US-061 — no currency symbol; grouped 2-decimal (matches @/lib/format).
function formatCents(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

interface CountTileProps {
  testId: string;
  label: string;
  count: number | null;
  loading: boolean;
  href: string;
  icon: (props: { className?: string }) => React.ReactNode;
  onRetry?: () => void;
  errored: boolean;
}

function CountTile({
  testId,
  label,
  count,
  loading,
  href,
  icon: Icon,
  onRetry,
  errored,
}: CountTileProps) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl bg-card border border-border p-5 shadow-elegant"
    >
      <div className="h-10 w-10 grid place-items-center rounded-xl bg-primary/10 text-primary">
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-sm text-muted-foreground mt-4">{label}</p>
      <div className="mt-1">
        {loading ? (
          <span className="inline-block h-7 w-12 rounded bg-muted/40 animate-pulse" />
        ) : errored ? (
          <div className="flex items-baseline gap-3">
            <span className="text-2xl font-bold tracking-tight">—</span>
            {onRetry && (
              <button onClick={onRetry} className="text-xs text-primary hover:underline">
                Retry
              </button>
            )}
          </div>
        ) : (
          <span className="text-2xl font-bold tracking-tight tabular-nums">{count ?? 0}</span>
        )}
      </div>
      <Link
        to={href}
        className="mt-3 inline-flex items-center gap-1 text-xs text-primary hover:underline font-medium"
      >
        View <ArrowUpRight className="h-3 w-3" />
      </Link>
    </div>
  );
}

function SpendTile({
  kpis,
  loading,
  errored,
  onRetry,
}: {
  kpis: DashboardKPIsDto | null;
  loading: boolean;
  errored: boolean;
  onRetry?: () => void;
}) {
  return (
    <div
      data-testid="kpi-tile-spend"
      className="rounded-2xl bg-card border border-border p-5 shadow-elegant"
    >
      <div className="h-10 w-10 grid place-items-center rounded-xl bg-primary/10 text-primary">
        <DollarSign className="h-5 w-5" />
      </div>
      <p className="text-sm text-muted-foreground mt-4">Spend This Period</p>
      <div className="mt-1">
        {loading ? (
          <span className="inline-block h-7 w-32 rounded bg-muted/40 animate-pulse" />
        ) : errored || !kpis ? (
          <div className="flex items-baseline gap-3">
            <span className="text-2xl font-bold tracking-tight">—</span>
            {onRetry && (
              <button onClick={onRetry} className="text-xs text-primary hover:underline">
                Retry
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="text-2xl font-bold tracking-tight tabular-nums">
              {formatCents(kpis.spend_this_period.total_amount)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {periodStartToSinceLabel(kpis.spend_this_period.period_start)}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function KpiTilesSection() {
  const [kpis, setKpis] = useState<DashboardKPIsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function fetchKpis() {
      // Mount-time fetch pattern (CLAUDE.md): await mswReady in dev so the
      // first request does not escape to the network before the SW boots.
      if (import.meta.env.DEV) {
        const { mswReady } = await import("@/mocks/browser");
        await mswReady;
      }
      setLoading(true);
      setErrored(false);
      try {
        const res = await fetchWithAuth("/api/dashboard/kpis");
        if (!res.ok) {
          if (!cancelled) setErrored(true);
          return;
        }
        const { data } = (await res.json()) as { data: DashboardKPIsDto };
        if (!cancelled) setKpis(data);
      } catch {
        if (!cancelled) setErrored(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchKpis();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const retry = () => setReloadKey((k) => k + 1);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <CountTile
        testId="kpi-tile-prs-pending"
        label="PRs Pending (System-wide)"
        count={kpis?.prs_pending.count ?? null}
        loading={loading}
        errored={errored}
        onRetry={retry}
        href="/purchase-requests?status=Submitted,ChangesRequested"
        icon={FileText}
      />
      <CountTile
        testId="kpi-tile-pos-outstanding"
        label="POs Outstanding (System-wide)"
        count={kpis?.pos_outstanding.count ?? null}
        loading={loading}
        errored={errored}
        onRetry={retry}
        href="/purchase-orders?status=Sent,PartiallyReceived"
        icon={ShoppingCart}
      />
      <CountTile
        testId="kpi-tile-invoices-awaiting"
        label="Invoices Awaiting Resolution (System-wide)"
        count={kpis?.invoices_awaiting_resolution.count ?? null}
        loading={loading}
        errored={errored}
        onRetry={retry}
        href="/invoices?status=Recorded,Exception,Held"
        icon={Wallet}
      />
      <SpendTile kpis={kpis} loading={loading} errored={errored} onRetry={retry} />
    </div>
  );
}

// US-049 / DASH-03 — canonical lifecycle order per entity. Tiles render in
// this order with zero-suppression (statuses absent from the response are
// skipped). Order matches the AC §"Status display order within each
// sub-section".
const PR_STATUS_ORDER = ["Draft", "Submitted", "ChangesRequested", "Approved", "Rejected"] as const;
const PO_STATUS_ORDER = ["Draft", "Sent", "PartiallyReceived", "Received", "Cancelled"] as const;
const INV_STATUS_ORDER = [
  "Recorded",
  "Matched",
  "Exception",
  "Held",
  "Approved",
  "Paid",
  "Rejected",
  "Cancelled",
] as const;

interface StatusTilesSubSectionProps {
  testId: string;
  heading: string;
  counts: Record<string, number>;
  order: ReadonlyArray<string>;
  resource: "purchase-requests" | "purchase-orders" | "invoices";
  entityKey: "prs" | "pos" | "invoices";
}

function StatusTilesSubSection({
  testId,
  heading,
  counts,
  order,
  resource,
  entityKey,
}: StatusTilesSubSectionProps) {
  const presentStatuses = order.filter((s) => (counts[s] ?? 0) > 0);
  return (
    <section
      data-testid={testId}
      className="rounded-2xl bg-card border border-border p-5 shadow-elegant space-y-4"
    >
      <h3 className="text-sm font-semibold">{heading}</h3>
      {presentStatuses.length === 0 ? (
        <p className="text-xs text-muted-foreground">No active documents.</p>
      ) : (
        <div className="flex flex-wrap gap-3">
          {presentStatuses.map((status) => (
            <Link
              key={status}
              to={`/${resource}` as "/purchase-requests" | "/purchase-orders" | "/invoices"}
              search={{ status }}
              data-testid={`status-tile-${entityKey}-${status}`}
              className="min-w-[120px] rounded-xl border border-border bg-muted/30 px-4 py-3 hover:bg-muted/60 transition"
            >
              <p className="text-xs text-muted-foreground">{status}</p>
              <p className="text-2xl font-bold tracking-tight tabular-nums mt-1">
                {counts[status] ?? 0}
              </p>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function StatusSummarySection() {
  const [summary, setSummary] = useState<StatusSummaryDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function fetchSummary() {
      if (import.meta.env.DEV) {
        const { mswReady } = await import("@/mocks/browser");
        await mswReady;
      }
      setLoading(true);
      setErrored(false);
      try {
        const res = await fetchWithAuth("/api/dashboard/status-summary");
        if (!res.ok) {
          if (!cancelled) setErrored(true);
          return;
        }
        const { data } = (await res.json()) as { data: StatusSummaryDto };
        if (!cancelled) setSummary(data);
      } catch {
        if (!cancelled) setErrored(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchSummary();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {["Purchase Requests", "Purchase Orders", "Invoices"].map((h) => (
          <div
            key={h}
            className="rounded-2xl bg-card border border-border p-5 shadow-elegant space-y-4"
          >
            <h3 className="text-sm font-semibold">{h}</h3>
            <div className="h-8 w-32 rounded bg-muted/40 animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  if (errored || !summary) {
    const retry = () => setReloadKey((k) => k + 1);
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {["Purchase Requests", "Purchase Orders", "Invoices"].map((h) => (
          <div
            key={h}
            className="rounded-2xl bg-card border border-border p-5 shadow-elegant space-y-2"
          >
            <h3 className="text-sm font-semibold">{h}</h3>
            <p className="text-xs text-muted-foreground">Could not load status data.</p>
            <button onClick={retry} className="text-xs text-primary hover:underline">
              Retry
            </button>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <StatusTilesSubSection
        testId="status-section-prs"
        heading="Purchase Requests"
        counts={summary.prs}
        order={PR_STATUS_ORDER}
        resource="purchase-requests"
        entityKey="prs"
      />
      <StatusTilesSubSection
        testId="status-section-pos"
        heading="Purchase Orders"
        counts={summary.pos}
        order={PO_STATUS_ORDER}
        resource="purchase-orders"
        entityKey="pos"
      />
      <StatusTilesSubSection
        testId="status-section-invoices"
        heading="Invoices"
        counts={summary.invoices}
        order={INV_STATUS_ORDER}
        resource="invoices"
        entityKey="invoices"
      />
    </div>
  );
}

const SPEND_BREAKDOWN_COLORS = [
  "var(--primary)",
  "#22c55e",
  "#f59e0b",
  "#ec4899",
  "#06b6d4",
  "#a855f7",
];

function defaultDateRange(): { date_from: string; date_to: string } {
  const today = new Date();
  const start = new Date(today);
  start.setUTCMonth(start.getUTCMonth() - 3);
  const fmt = (d: Date) =>
    `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(
      d.getUTCDate(),
    ).padStart(2, "0")}`;
  return { date_from: fmt(start), date_to: fmt(today) };
}

function SpendBreakdownSection() {
  const [groupBy, setGroupBy] = useState<"cost_centre" | "vendor">("cost_centre");
  const [data, setData] = useState<SpendBreakdownDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (import.meta.env.DEV) {
        const { mswReady } = await import("@/mocks/browser");
        await mswReady;
      }
      setLoading(true);
      setErrored(false);
      const { date_from, date_to } = defaultDateRange();
      try {
        const res = await fetchWithAuth(
          `/api/dashboard/spend-breakdown?group_by=${groupBy}&date_from=${date_from}&date_to=${date_to}&limit=5`,
        );
        if (!res.ok) {
          if (!cancelled) setErrored(true);
          return;
        }
        const body = (await res.json()) as { data: SpendBreakdownDto };
        if (!cancelled) setData(body.data);
      } catch {
        if (!cancelled) setErrored(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [groupBy]);

  const items = data?.items ?? [];
  const chartData = items.map((item, i) => ({
    name: item.label,
    value: item.total_amount,
    color: SPEND_BREAKDOWN_COLORS[i % SPEND_BREAKDOWN_COLORS.length],
  }));
  const grandTotal = data?.currency_total ?? 0;

  return (
    <div
      data-testid="spend-breakdown-section"
      className="rounded-2xl bg-card border border-border p-5 shadow-elegant"
    >
      <div className="mb-4 flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">Spend Breakdown</h3>
          <p className="text-xs text-muted-foreground">Last 3 months</p>
        </div>
        <div className="inline-flex rounded-lg border border-border bg-muted/30 p-0.5 text-xs">
          <button
            type="button"
            onClick={() => setGroupBy("cost_centre")}
            className={`px-2.5 py-1 rounded-md transition ${
              groupBy === "cost_centre"
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Cost Centre
          </button>
          <button
            type="button"
            onClick={() => setGroupBy("vendor")}
            className={`px-2.5 py-1 rounded-md transition ${
              groupBy === "vendor"
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Vendor
          </button>
        </div>
      </div>

      {loading ? (
        <div className="h-[180px] grid place-items-center text-xs text-muted-foreground">
          Loading…
        </div>
      ) : errored ? (
        <div className="h-[180px] grid place-items-center text-xs text-destructive">
          Could not load spend breakdown.
        </div>
      ) : items.length === 0 ? (
        <div className="h-[180px] grid place-items-center text-xs text-muted-foreground">
          No invoiced spend in range.
        </div>
      ) : (
        <>
          {/* US-048 — horizontal bar chart. Bars ordered longest-first; axis +
              inline labels use the K/M abbreviated formatter; tooltip surfaces
              full precision + invoice_count for the "Other" group as well. */}
          <ResponsiveContainer width="100%" height={Math.max(180, items.length * 36)}>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 4, right: 36, left: 0, bottom: 4 }}
            >
              <CartesianGrid horizontal={false} stroke="var(--border)" strokeDasharray="2 4" />
              <XAxis
                type="number"
                tickFormatter={(v: number) => formatCompactCurrency(v)}
                tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                axisLine={{ stroke: "var(--border)" }}
                tickLine={false}
              />
              <YAxis
                type="category"
                dataKey="name"
                width={130}
                tick={{ fontSize: 11, fill: "var(--foreground)" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v: string) => (v.length > 24 ? `${v.slice(0, 22)}…` : v)}
              />
              <Tooltip
                contentStyle={{
                  background: "var(--popover)",
                  border: "1px solid var(--border)",
                  borderRadius: 12,
                  fontSize: 12,
                }}
                formatter={(value: number) => formatCents(value)}
                labelFormatter={(label: string) => label}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {chartData.map((c, i) => (
                  <Cell key={i} fill={c.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <p className="mt-3 text-xs text-muted-foreground">
            Total approved spend:{" "}
            <span className="font-medium text-foreground">{formatCents(grandTotal)}</span>
          </p>
        </>
      )}
    </div>
  );
}

// US-999 — Recent Purchase Requests, wired to GET /api/purchase-requests.
// Replaces the Lovable static table (invented "Aarav Sharma" rows). The
// handler scopes by role (a Requester sees their own; approvers see all).
function RecentPRsSection() {
  const [rows, setRows] = useState<PurchaseRequestListItemDto[] | null>(null);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (import.meta.env.DEV) {
        const { mswReady } = await import("@/mocks/browser");
        await mswReady;
      }
      try {
        const res = await fetchWithAuth("/api/purchase-requests?page=1&limit=5");
        if (!res.ok) {
          if (!cancelled) setErrored(true);
          return;
        }
        const { data } = (await res.json()) as { data: PurchaseRequestListItemDto[] };
        if (!cancelled) setRows(data);
      } catch {
        if (!cancelled) setErrored(true);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      data-testid="dashboard-recent-prs"
      className="lg:col-span-2 rounded-2xl bg-card border border-border shadow-elegant overflow-hidden"
    >
      <div className="flex items-center justify-between p-5 border-b border-border">
        <div>
          <h3 className="text-sm font-semibold">Recent Purchase Requests</h3>
          <p className="text-xs text-muted-foreground">Latest requests you can see</p>
        </div>
        <Link
          to="/purchase-requests"
          className="text-xs font-medium text-primary inline-flex items-center gap-1 hover:underline"
        >
          View all <ArrowUpRight className="h-3 w-3" />
        </Link>
      </div>
      <div className="overflow-x-auto">
        {rows === null && !errored ? (
          <div className="p-6 text-sm text-muted-foreground">Loading…</div>
        ) : errored ? (
          <div className="p-6 text-sm text-destructive">Could not load purchase requests.</div>
        ) : rows && rows.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">No purchase requests yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs text-muted-foreground">
              <tr>
                <th className="text-left font-medium px-5 py-2.5">Request</th>
                <th className="text-right font-medium px-3 py-2.5">Amount</th>
                <th className="text-left font-medium px-5 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {(rows ?? []).map((pr) => (
                <tr key={pr.id} className="border-t border-border hover:bg-accent/40 transition">
                  <td className="px-5 py-3">
                    <Link to="/purchase-requests/$id" params={{ id: pr.id }} className="block">
                      <p className="font-medium truncate max-w-[260px] hover:text-primary transition-colors">
                        {pr.title}
                      </p>
                      <p className="text-xs text-muted-foreground font-mono">
                        {pr.pr_number} · {formatDate(pr.created_at)}
                      </p>
                    </Link>
                  </td>
                  <td className="px-3 py-3 text-right font-medium tabular-nums">
                    {formatAmount(pr.total_amount)}
                  </td>
                  <td className="px-5 py-3">
                    <TransactionStatusBadge config={PR_STATUS_CONFIG[pr.status]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// US-999 — Notifications panel, wired to GET /api/notifications (per-user).
// Replaces the Lovable static notification list.
function NotificationsSection() {
  const [items, setItems] = useState<NotificationDto[] | null>(null);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (import.meta.env.DEV) {
        const { mswReady } = await import("@/mocks/browser");
        await mswReady;
      }
      try {
        const res = await fetchWithAuth("/api/notifications");
        if (!res.ok) {
          if (!cancelled) setErrored(true);
          return;
        }
        const { data } = (await res.json()) as { data: NotificationDto[] };
        if (!cancelled) setItems(data);
      } catch {
        if (!cancelled) setErrored(true);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      data-testid="dashboard-notifications"
      className="rounded-2xl bg-card border border-border p-5 shadow-elegant"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold">Notifications</h3>
        <Link to="/notifications" className="text-xs text-primary hover:underline">
          See all
        </Link>
      </div>
      {items === null && !errored ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : errored ? (
        <p className="text-sm text-destructive">Could not load notifications.</p>
      ) : items && items.length === 0 ? (
        <p className="text-sm text-muted-foreground">You're all caught up.</p>
      ) : (
        <ul className="space-y-2.5">
          {(items ?? []).slice(0, 4).map((n) => (
            <li key={n.id} className="flex gap-3 p-2 -mx-2 rounded-lg hover:bg-accent transition">
              <span
                className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${
                  !n.is_read ? "bg-primary" : "bg-muted-foreground/30"
                }`}
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{n.message}</p>
                <p className="text-xs text-muted-foreground">{formatDate(n.created_at)}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Dashboard() {
  const { user } = useAuth();
  const firstName = user?.name.split(" ")[0] ?? "there";
  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Good morning, {firstName} 👋</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Here's what's happening across procurement today.
        </p>
      </div>

      {/* US-047 / DASH-01 KPI tiles — system-wide P2P operational counts. */}
      <KpiTilesSection />

      {/* US-049 / DASH-03 status summary — zero-suppressed status counts per entity. */}
      <StatusSummarySection />

      {/* US-048 / DASH-02 spend breakdown — live, from /api/dashboard/spend-breakdown. */}
      <SpendBreakdownSection />

      {/* US-999 — live recent PRs + notifications (replaces Lovable mock sections). */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <RecentPRsSection />
        <NotificationsSection />
      </div>
    </div>
  );
}
